import { useEffect, useState } from "react";
import axios from "axios";
import { LayoutDashboard, ArrowLeftRight, HandCoins, Receipt, Landmark, Car, ScrollText, BarChart3, Settings2, PieChart, Trophy } from "lucide-react";
import { API, authHeaders } from "./shared";
import FinanceDashboard from "./FinanceDashboard";
import TransactionsLedger from "./TransactionsLedger";
import ManualRevenueModule from "./ManualRevenueModule";
import ExpensesModule from "./ExpensesModule";
import DepositsModule from "./DepositsModule";
import AuditLogModule from "./AuditLogModule";
import ReportsModule from "./ReportsModule";
import PricingSettingsModule from "./PricingSettingsModule";
import RevenueMixModule from "./RevenueMixModule";
import PricingPerformanceModule from "./PricingPerformanceModule";
import FleetLeaderboardModule from "./FleetLeaderboardModule";
import DynamicPricingModule from "./DynamicPricingModule";
import ExtensionAnalyticsModule from "./ExtensionAnalyticsModule";
import SquareSyncModule from "./SquareSyncModule";
import PaymentMethodReport from "./PaymentMethodReport";
import CardFeeComplianceReport from "./CardFeeComplianceReport";
import RevenueReportModule from "./RevenueReportModule";
import DuplicateAuditModule from "./DuplicateAuditModule";
import { ReconciliationAuditCard } from "./ReconciliationAuditCard";
import { LiveCorrectionTool } from "./LiveCorrectionTool";
import { DayCountAuditCard } from "./DayCountAuditCard";
import { BookingPaymentAuditCard } from "./BookingPaymentAuditCard";
import MoneyRepairCard from "./MoneyRepairCard";
import HamptonPaymentLedgerFixCard from "@/components/admin/HamptonPaymentLedgerFixCard";
import ExpenseDashboard from "./ExpenseDashboard";
import LedgerManager from "../LedgerManager";
import { Zap, CreditCard, Wallet } from "lucide-react";

const MODULES = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "reconciliation", label: "Reconciliation Audit", icon: ScrollText },
  { key: "money", label: "My Money", icon: LayoutDashboard },
  { key: "pricing", label: "Pricing Settings", icon: Settings2 },
  { key: "dynamic", label: "Dynamic Pricing", icon: Zap },
  { key: "revenue-mix", label: "Revenue Mix", icon: PieChart },
  { key: "performance", label: "Pricing Performance", icon: BarChart3 },
  { key: "leaderboard", label: "Fleet Leaderboard", icon: Trophy },
  { key: "revenue-report", label: "Revenue Report", icon: PieChart },
  { key: "transactions", label: "Transactions", icon: ArrowLeftRight },
  { key: "dup-audit", label: "Duplicate Audit", icon: ArrowLeftRight },
  { key: "square", label: "Square", icon: CreditCard },
  { key: "paymethods", label: "Pay Methods", icon: Wallet },
  { key: "card-fee", label: "Card Fee Compliance", icon: CreditCard },
  { key: "extensions", label: "Extensions", icon: BarChart3 },
  { key: "revenue", label: "Manual Revenue", icon: HandCoins },
  { key: "expenses", label: "Expenses", icon: Receipt },
  { key: "deposits", label: "Bank Deposits", icon: Landmark },
  { key: "reports", label: "Reports", icon: BarChart3 },
  { key: "vehicles", label: "Vehicle Ledger", icon: Car },
  { key: "audit", label: "Audit Log", icon: ScrollText },
];

export default function FinanceCenter({ token, onAuthError }) {
  const [mod, setMod] = useState("dashboard");
  const [options, setOptions] = useState(null);
  const [vehicles, setVehicles] = useState([]);

  useEffect(() => {
    axios.get(`${API}/finance/options`, authHeaders(token)).then((r) => setOptions(r.data))
      .catch((e) => { if (e.response?.status === 401) onAuthError?.(); });
    axios.get(`${API}/vehicles`).then((r) => setVehicles(r.data || []));
    // eslint-disable-next-line
  }, []);

  return (
    <div data-testid="finance-center">
      <div className="mb-5">
        <h2 className="text-xl font-bold text-white">Financial Management Center</h2>
        <p className="text-white/50 text-sm">Every dollar in and out of AutoHire — online payments, manual revenue, expenses, and bank deposits in one place.</p>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {MODULES.map((m) => (
          <button key={m.key} data-testid={`finance-module-${m.key}`} onClick={() => setMod(m.key)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
              mod === m.key ? "bg-[#F2B705] text-black" : "bg-[#0C0C0C] border border-white/10 text-white/65 hover:text-white"}`}>
            <m.icon size={15} /> {m.label}
          </button>
        ))}
      </div>

      {mod === "dashboard" && <FinanceDashboard token={token} onAuthError={onAuthError} />}
      {mod === "reconciliation" && <ReconciliationAuditCard token={token} />}
      {mod === "pricing" && <PricingSettingsModule token={token} />}
      {mod === "dynamic" && <DynamicPricingModule token={token} />}
      {mod === "revenue-mix" && <RevenueMixModule token={token} />}
      {mod === "performance" && <PricingPerformanceModule token={token} />}
      {mod === "leaderboard" && <FleetLeaderboardModule token={token} />}
      {mod === "extensions" && <ExtensionAnalyticsModule token={token} />}
      {mod === "transactions" && <TransactionsLedger token={token} onAuthError={onAuthError} options={options} />}
      {mod === "revenue-report" && <RevenueReportModule token={token} onAuthError={onAuthError} />}
      {mod === "square" && <SquareSyncModule token={token} />}
      {mod === "dup-audit" && (
        <div className="space-y-6">
          <BookingPaymentAuditCard token={token} />
          <DayCountAuditCard token={token} />
          <LiveCorrectionTool token={token} />
          <HamptonPaymentLedgerFixCard bookingId="ad3d3d95-7da8-4b0c-a4aa-57031a873596" token={token} onApplied={() => {}} />
          <MoneyRepairCard token={token} />
          <DuplicateAuditModule token={token} />
        </div>
      )}
      {mod === "money" && <ExpenseDashboard token={token} />}
      {mod === "paymethods" && <PaymentMethodReport token={token} />}
      {mod === "card-fee" && <CardFeeComplianceReport token={token} />}
      {mod === "revenue" && <ManualRevenueModule token={token} onAuthError={onAuthError} options={options} vehicles={vehicles} />}
      {mod === "expenses" && <ExpensesModule token={token} onAuthError={onAuthError} options={options} vehicles={vehicles} />}
      {mod === "deposits" && <DepositsModule token={token} onAuthError={onAuthError} options={options} />}
      {mod === "reports" && <ReportsModule token={token} onAuthError={onAuthError} />}
      {mod === "vehicles" && <LedgerManager token={token} onAuthError={onAuthError} />}
      {mod === "audit" && <AuditLogModule token={token} onAuthError={onAuthError} />}
    </div>
  );
}
